import { InlineNotification } from '@carbon/react';
import { DataTable, DataTableColumnDef } from 'components/data-table';
import { Link } from 'components/link/link';
import { useValidityStatusConfig } from 'components/structure-grids/validity-status-indicator';
import { useAtom } from 'jotai';
import { usePolling } from 'lib/hooks/usePolling';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { formatMutationResource } from 'utils/misc';
import { parseUTCdateTime } from 'utils/dates';
import { format0 } from 'utils/number';
import { capitalizeFirstLetter } from 'utils/strings';
import { useWellLandingAnalysisWellsAtom } from '../atoms';
import styles from './landing-analysis-accordion.module.scss';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface WellLandingAnalysisRow extends Record<string, unknown> {
  id: string;
  uwi14: string;
  status: string;
  executionStatus: string;
  jobResult: string;
  validWellboreGeometry: string;
  wellName: string;
  currentOperator: string;
  wellStatus: string;
  holeDirection: string;
  landingZone: string;
  trueVerticalDepthFt: string;
  lateralLengthFt: string;
  permitDate: string;
  spudDate: string;
  landingAnalysisFlags: string;
}

const LandingResultCell = ({ value }: { value: string }) => {
  const config = useValidityStatusConfig(value.toLowerCase());
  if (!value || value === '—') return <span>—</span>;
  return (
    <span className={styles.jobResultCell}>
      {config.icon}
      {capitalizeFirstLetter(value)}
    </span>
  );
};

// ─── Column Definitions ───────────────────────────────────────────────────────

const WELL_LANDING_ANALYSIS_COLUMNS: DataTableColumnDef<WellLandingAnalysisRow>[] =
  [
    {
      id: 'uwi14',
      accessorKey: 'uwi14',
      header: 'UWI 14',
      size: 170,
      cell: (info) => {
        const uwi14 = info.getValue() as string;
        const wellId = uwi14.slice(0, 10);
        return <Link to={`/wells/${wellId}`}>{uwi14}</Link>;
      },
    },
    {
      id: 'executionStatus',
      accessorKey: 'executionStatus',
      header: 'Execution Status',
      size: 150,
      meta: { type: 'text' },
    },
    {
      id: 'jobResult',
      accessorKey: 'jobResult',
      header: 'Job Result',
      size: 140,
      cell: (info) => (
        <LandingResultCell value={(info.getValue() as string) ?? '—'} />
      ),
    },
    {
      id: 'validWellboreGeometry',
      accessorKey: 'validWellboreGeometry',
      header: 'Valid Wellbore Geometry',
      size: 190,
      meta: { type: 'text' },
    },
    {
      id: 'wellName',
      accessorKey: 'wellName',
      header: 'Well Name',
      size: 180,
      meta: { type: 'text' },
    },
    {
      id: 'currentOperator',
      accessorKey: 'currentOperator',
      header: 'Current Operator',
      size: 185,
      meta: { type: 'text' },
    },
    {
      id: 'wellStatus',
      accessorKey: 'wellStatus',
      header: 'Well Status',
      size: 120,
      meta: { type: 'text' },
    },
    {
      id: 'holeDirection',
      accessorKey: 'holeDirection',
      header: 'Reported Hole Direction',
      size: 140,
      meta: { type: 'text' },
    },
    {
      id: 'landingZone',
      accessorKey: 'landingZone',
      header: 'Landing Zone',
      size: 140,
      meta: { type: 'text' },
    },
    {
      id: 'trueVerticalDepthFt',
      accessorKey: 'trueVerticalDepthFt',
      header: 'True Vertical Depth (TVDSS, ft)',
      size: 195,
      meta: { type: 'text' },
    },
    {
      id: 'lateralLengthFt',
      accessorKey: 'lateralLengthFt',
      header: 'Lateral Length (ft)',
      size: 160,
      meta: { type: 'text' },
    },
    {
      id: 'permitDate',
      accessorKey: 'permitDate',
      header: 'Permit Date',
      size: 120,
      meta: { type: 'text' },
    },
    {
      id: 'spudDate',
      accessorKey: 'spudDate',
      header: 'Spud Date',
      size: 120,
      meta: { type: 'text' },
    },
    {
      id: 'landingAnalysisFlags',
      accessorKey: 'landingAnalysisFlags',
      header: 'Landing Analysis Flags',
      size: 180,
      meta: { type: 'text' },
    },
  ];

// ─── Component ────────────────────────────────────────────────────────────────

interface WellLandingAnalysesTableProps {
  requestId: number;
  wellsQueryHref: string;
  /** Parent request execution status (e.g. "Processed"); drives the indexing catch-up state. */
  requestStatus: string;
  /** Analyses the run produced (from the request row); target for ES to converge to. */
  expectedResultCount: number;
}

export const WellLandingAnalysesTable = ({
  requestId,
  wellsQueryHref,
  requestStatus,
  expectedResultCount,
}: WellLandingAnalysesTableProps) => {
  const analysesAtom = useWellLandingAnalysisWellsAtom();
  const [analysesQueryState, fetchAnalyses] = useAtom(analysesAtom);
  const { loading: isLoadingAnalyses, data: analysesData } =
    formatMutationResource(analysesQueryState);
  const analysesError =
    analysesQueryState.result?.type === 'error' ||
    analysesQueryState.result?.type === 'fatal'
      ? 'Failed to load well landing analyses'
      : null;

  const [analysesSearch, setAnalysesSearch] = useState('');
  const [columnFilters, setColumnFilters] = useState<
    { id: string; value: unknown }[]
  >([]);

  const loadAnalysesPage = useCallback(
    ({
      page,
      pageSize,
      text,
    }: {
      page: number;
      pageSize: number;
      text?: string;
    }) => {
      fetchAnalyses({
        url: wellsQueryHref,
        type: 'POST',
        data: {
          wellLandingAnalysisRequestId: requestId,
          page,
          pageSize,
          sortAttribute: 'uwi14',
          sortDirection: 'asc',
          ...(text ? { text } : {}),
        },
      });
    },
    [requestId, wellsQueryHref, fetchAnalyses]
  );

  // requestStatus is a dependency so the list refetches the moment the run flips to
  // processed (the parent re-renders this expansion with the new status), rather than
  // waiting for the next poll tick.
  useEffect(() => {
    loadAnalysesPage({
      page: 1,
      pageSize: 50,
      text: analysesSearch || undefined,
    });
  }, [loadAnalysesPage, analysesSearch, requestStatus]);

  const analyses: WellLandingAnalysisRow[] = (analysesData?.results ?? []).map(
    (w) => ({
      id: String(w.id),
      uwi14: w.uwi14,
      status: capitalizeFirstLetter(w.status),
      executionStatus: w.executionStatus
        ? capitalizeFirstLetter(w.executionStatus)
        : '—',
      jobResult: w.jobResult ? capitalizeFirstLetter(w.jobResult) : '—',
      validWellboreGeometry: w.validWellBoreGeometry ? 'Yes' : 'No',
      wellName: w.wellName,
      currentOperator: w.currentOperator,
      wellStatus: w.wellStatus,
      holeDirection: w.holeDirection,
      landingZone: w.landingInterval ?? '—',
      trueVerticalDepthFt:
        w.trueVerticalDepthFt !== null
          ? `${format0(w.trueVerticalDepthFt ?? 0)} ft`
          : '—',
      lateralLengthFt:
        w.lateralLengthFt !== null
          ? `${format0(w.lateralLengthFt ?? 0)} ft`
          : '—',
      permitDate: w.permitDate ? parseUTCdateTime(w.permitDate) : '—',
      spudDate: w.spudDate ? parseUTCdateTime(w.spudDate) : '—',
      landingAnalysisFlags: w.landingAnalysisFlags ?? '—',
    })
  );

  const filteredAnalyses = useMemo(() => {
    if (columnFilters.length === 0) return analyses;
    return analyses.filter((row) =>
      columnFilters.every((filter) => {
        const value = row[filter.id as keyof WellLandingAnalysisRow];
        return String(value ?? '')
          .toLowerCase()
          .includes(String(filter.value ?? '').toLowerCase());
      })
    );
  }, [analyses, columnFilters]);

  // ─── Results-not-ready state ────────────────────────────────────────────────
  // The ES-backed list is empty while a run is in progress (per-record indexing is
  // suppressed during the run) and for a short window after it completes (the bulk
  // reindex is still draining). In both cases show a status message instead of the
  // bare "No results" table. Once the run is complete, poll until ES catches up,
  // bounded by MAX_INDEXING_POLLS so it can't loop forever if the counts legitimately
  // differ. No polling while the run is still active — the parent re-renders this
  // expansion with the new status on completion, which is what starts the catch-up.
  const MAX_INDEXING_POLLS = 24; // ~2 min at 5s
  const [indexingPolls, setIndexingPolls] = useState(0);

  const status = requestStatus.toLowerCase();
  const requestActive = status === 'pending' || status === 'processing';
  const requestComplete = status === 'processed' || status === 'failed';
  const totalCount = analysesData?.totalCount ?? 0;
  const hasFetched = Boolean(analysesQueryState.result);
  const behindExpected =
    hasFetched && expectedResultCount > 0 && totalCount < expectedResultCount;

  const resultsPending = behindExpected && (requestActive || requestComplete);
  const isIndexing = behindExpected && requestComplete;

  usePolling({
    fn: () => {
      setIndexingPolls((n) => n + 1);
      loadAnalysesPage({
        page: 1,
        pageSize: 50,
        text: analysesSearch || undefined,
      });
    },
    intervalMs: 5000,
    enabled: isIndexing && indexingPolls < MAX_INDEXING_POLLS,
    immediate: false,
  });

  return (
    <div className={styles.wellsSubTable}>
      {resultsPending && (
        <InlineNotification
          kind="info"
          lowContrast
          hideCloseButton
          title={
            isIndexing
              ? 'Results are being indexed'
              : 'Landing analysis in progress'
          }
          subtitle={
            isIndexing
              ? 'Processing finished. Results are still being persisted to search and will appear here shortly.'
              : 'Wells are still being processed. Results will appear here as they complete.'
          }
        />
      )}
      <DataTable<WellLandingAnalysisRow>
        data={filteredAnalyses}
        columns={WELL_LANDING_ANALYSIS_COLUMNS}
        getRowId={(row) => row.id}
        isLoading={isLoadingAnalyses}
        error={analysesError}
        searchPlaceholder="Search well landing analyses..."
        scroll={{
          mode: 'paginated',
          serverDriven: true,
          totalCount: analysesData?.totalCount ?? 0,
        }}
        onPaginationChange={({ page, pageSize }) =>
          loadAnalysesPage({
            page,
            pageSize,
            text: analysesSearch || undefined,
          })
        }
        onGlobalSearchChange={(s) => setAnalysesSearch(s.globalFilter)}
        onFilterChange={(s) => setColumnFilters(s.columnFilters)}
        config={{
          columnResizing: true,
          sorting: true,
          globalSearch: true,
          columnFilters: true,
          columnVisibility: true,
          columnPinning: true,
          columnReordering: true,
        }}
      />
    </div>
  );
};
